import { useState, useEffect, useCallback } from 'react'
import { fetchGalleryPhotos, uploadPhoto, deletePhoto } from '../api/galleryApi'
import { appToast } from '../../../../components/AppToast'
import { appConfirm } from '../../../../components/AppConfirmModal'

export default function useGallery() {
  const [photos, setPhotos] = useState([])
  const [loading, setLoading] = useState(false)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [selectedPhoto, setSelectedPhoto] = useState(null)
  const [isSaving, setIsSaving] = useState(false)

  const loadPhotos = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetchGalleryPhotos()
      setPhotos(res.photos)
    } catch (e) {
      console.error('Failed to load gallery photos', e)
      appToast.error('Failed to load gallery photos.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadPhotos()
  }, [loadPhotos])

  useEffect(() => {
    const handler = () => loadPhotos()
    window.addEventListener('cloud-gallery-updated', handler)
    return () => window.removeEventListener('cloud-gallery-updated', handler)
  }, [loadPhotos])

  const handleUpload = async ({ imageUrl, caption }) => {
    if (!imageUrl) {
      appToast.error('Please select an image file to upload.')
      return false
    }

    setIsSaving(true)
    try {
      await uploadPhoto({ imageUrl, caption })
      appToast.success('Image saved Successfully')
      setIsUploadModalOpen(false)
      loadPhotos()
      return true
    } catch (e) {
      console.error('Failed to save image', e)
      appToast.error(e.message || 'Error saving Image')
      return false
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (id) => {
    const ok = await appConfirm({
      title: 'Delete Picture',
      message: 'Are you sure you want to delete this picture?',
      confirmText: 'Delete',
      isDestructive: true,
    })
    if (ok) {
      try {
        await deletePhoto(id)
        appToast.success('Picture deleted successfully.')
        if (selectedPhoto && selectedPhoto.id === id) {
          setSelectedPhoto(null)
        }
        loadPhotos()
      } catch (e) {
        console.error('Failed to delete picture', e)
        appToast.error('Error deleting picture.')
      }
    }
  }

  return {
    state: {
      photos,
      loading,
      isUploadModalOpen,
      selectedPhoto,
      isSaving,
      count: photos.length,
    },
    actions: {
      setIsUploadModalOpen,
      setSelectedPhoto,
      handleUpload,
      handleDelete,
      loadPhotos,
    },
  }
}
